import { AcsElement, CardAnswer } from '../../models/quiz-builder-pro.model';
import { DraftAnswer, DraftQuestionType } from '../../models/quiz-builder.model';

export const TYPE_LABELS: Record<DraftQuestionType, string> = { TF: 'True/False', MC: 'Multiple choice', MA: 'Multiple answer' };

export const PRO_QUIZZES_KEY = 'aviator.prototype.quizBuilderPro.quizzes';
export const QUESTION_VIEW_KEY = 'aviator.prototype.quizBuilderPro.questionView';

/**
 * Elements of an ACS test, each with the share of FAA written results for that test that missed it.
 * `missedCodeIds` holds one AcsCodeId per missed code per written result.
 */
export function acsElements(codes: { id: number; code: string; description: string; area?: string; task?: string }[],
                            resultCount: number, missedCodeIds: number[]): AcsElement[] {
  const misses = new Map<number, number>();
  missedCodeIds.forEach(id => misses.set(id, (misses.get(id) || 0) + 1));
  return codes.map(c => ({
    code: c.code,
    description: c.description,
    missRate: resultCount ? Math.round(Math.min(misses.get(c.id) || 0, resultCount) * 100 / resultCount) : null,
    ...(c.area ? { area: c.area } : {}),
    ...(c.task ? { task: c.task } : {})
  }));
}

/** Most-missed elements first; never-missed ones are not weak areas. */
export function weakAreas(elements: AcsElement[], limit = 6): AcsElement[] {
  return elements.filter(e => e.missRate > 0).sort((a, b) => b.missRate - a.missRate || a.code.localeCompare(b.code)).slice(0, limit);
}

/** Fisher-Yates copy. Models put the correct answer first, so generated answers are shuffled before display. */
export function shuffled<T>(items: T[], random: () => number = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

export function confidenceLabel(noul: number | null): string {
  return noul == null ? '' : `${Math.round(noul * 100)}%`;
}

export function loadJson<T>(key: string, fallback: T): T {
  try {
    const value = JSON.parse(localStorage.getItem(key));
    return value == null ? fallback : value;
  } catch {
    return fallback;
  }
}

/** A, B, C… in display order. */
export function cardAnswers(answers: DraftAnswer[]): CardAnswer[] {
  return answers.map((a, i) => ({ letter: String.fromCharCode(65 + i), text: a.text, correct: a.correct }));
}
